import { Size as ScreenSize } from '@ps/web-core-enums';
import { Translation, useBodyScrollLock, useScreenSize } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useRef } from 'react';
import type { FormRenderProps } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import { ExtraWalletsInfoBar } from '@swf/sports/components/ExtraWalletsInfoBar/ExtraWalletsInfoBar';
import useOnNavigate from '@swf/sports/hooks/useOnNavigate';

import useCloseOnEmpty from '../../hooks/useCloseOnEmpty';
import useCloseReceiptOnLogout from '../../hooks/useCloseReceiptOnLogout';
import useDismissibleBetSlipError from '../../hooks/useDismissibleBetSlipError';
import useOnMobileBreakpointChange from '../../hooks/useOnMobileBreakpointChange';
import useOpenReceiptOnMobile from '../../hooks/useOpenReceiptOnMobile';
import type { FormData } from '../../types';
import BetSlipFooter from '../Footer/Footer';
import Title from '../Title/Title';

import {
  TXTWCL_Bet_Placed_2,
  TXTWCL_Bet_Receipt,
  TXTWCL_Bets_Placed_1,
  TXTWCL_Multiples,
  TXTWCL_Singles,
  TXTWCL_Your_bet_slip_is_empty
} from './BetSlip.messages.json';
import styles from './BetSlip.scss';
import BetReceiptFooter from './components/BetReceipt/components/Footer/Footer';
import BetReceiptHeader from './components/BetReceipt/components/Header/Header';
import ReceiptItems from './components/BetReceipt/components/ReceiptItems/ReceiptItems';
import Totals from './components/BetReceipt/components/Totals/Totals';
import BetSlipHeader from './components/BetSlipHeader/BetSlipHeader';
import MultipleOpportunities from './components/Opportunities/MultipleOpportunities';
import SingleOpportunities from './components/Opportunities/SingleOpportunities';
import SportsRulesLink from './components/SportsRulesLink/SportsRulesLink';
import StaticMessages from './components/StaticMessages/StaticMessages';

const BetSlip: React.FC<FormRenderProps<FormData>> = ({ handleSubmit }) => {
  const betSlipElement = useRef<HTMLDivElement>(null);
  const { contentTranslationTag } = useDismissibleBetSlipError();
  const isBetReceiptActive = useSelector(select.betSlip.isBetReceiptActive);
  const receiptItems = useSelector(select.betSlip.receipt);
  const { betSlip } = useDispatch<Dispatch>();
  const hasSelections = useSelector(select.betSlip.hasSelections);
  const isBetSlipOpen = useSelector(select.betSlip.isOpen);
  const { greaterThan } = useScreenSize();

  const countOfBetsPlaced = receiptItems.SINGLE.length + receiptItems.MULTIPLE.length;
  const isSingleBet = countOfBetsPlaced === 1;
  const isMobile = !greaterThan(ScreenSize.Small);
  const isMobileHeaderVisible = hasSelections || !!contentTranslationTag;
  const isBetSlipHidden = isBetReceiptActive
    ? isMobile && !countOfBetsPlaced
    : isMobile && !isMobileHeaderVisible;

  useOpenReceiptOnMobile();

  useCloseReceiptOnLogout();

  useCloseOnEmpty();

  // Close when navigating
  useOnNavigate(() => betSlip.betSlipOpened(false));

  // Lock body scroll when the layout is open on a small screen
  useBodyScrollLock(isBetSlipOpen, betSlipElement.current);

  // Close when resizing to bigger than mobile
  useOnMobileBreakpointChange(
    useCallback((isMobile: boolean) => !isMobile && betSlip.betSlipOpened(false), [betSlip])
  );

  return (
    <form
      className={classNames(
        styles.layout,
        {
          [styles.betSlipOpen]: isBetSlipOpen,
          [styles.betSlipHidden]: isBetSlipHidden
        },
        styles.wrapper
      )}
      onSubmit={handleSubmit}
      data-testid={isBetReceiptActive ? 'bet-receipt' : 'bet-slip'}
    >
      <div className={classNames(styles.header)}>
        {isBetReceiptActive ? (
          <BetReceiptHeader
            title={<Translation {...(isSingleBet ? TXTWCL_Bet_Placed_2 : TXTWCL_Bets_Placed_1)} />}
          />
        ) : (
          <BetSlipHeader />
        )}
      </div>
      <div className={styles.betSlipContent} ref={betSlipElement}>
        {isBetReceiptActive ? (
          <>
            <Totals />
            <div className={styles.title}>
              <Translation {...TXTWCL_Bet_Receipt} />
            </div>
            <ReceiptItems
              open
              title={
                <Title count={receiptItems.MULTIPLE.length || undefined}>
                  <Translation {...TXTWCL_Multiples} />
                </Title>
              }
              receiptItems={receiptItems.MULTIPLE}
            />
            <ReceiptItems
              open
              title={
                <Title count={receiptItems.SINGLE.length || undefined}>
                  <Translation {...TXTWCL_Singles} />
                </Title>
              }
              receiptItems={receiptItems.SINGLE}
            />
          </>
        ) : (
          <>
            <ExtraWalletsInfoBar />
            <StaticMessages />
            {hasSelections ? (
              <>
                <MultipleOpportunities />
                <SingleOpportunities />
                <SportsRulesLink />
              </>
            ) : (
              <div className={styles.emptyBetSlip}>
                <Translation {...TXTWCL_Your_bet_slip_is_empty} />
              </div>
            )}
          </>
        )}
      </div>

      {isBetReceiptActive ? <BetReceiptFooter /> : <BetSlipFooter />}
    </form>
  );
};

export default BetSlip;
